import { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import NavbarAdmin from "../components/NavbarAdmin";
import Toast from "../components/Toast";
import SearchableSelect from "../components/SearchableSelect";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import {
  GUEST_TYPES, TH_PROVINCES,
  guestTypeLabel, guestTypeColor, stayPurposeLabel,
} from "../constants/guest";

const API = process.env.REACT_APP_API_URL || "";
const getToken = () => localStorage.getItem("token");
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });
const jsonHeader = () => ({ ...authHeader(), "Content-Type": "application/json" });

const PAGE_SIZE = 20;

/* ── styles (ตาม pattern ของหน้า admin อื่นๆ — inline object ไม่มี CSS file) ── */
const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db",
  fontSize: 13.5, background: "#fff", color: "#111827", boxSizing: "border-box",
  outline: "none", fontFamily: "inherit",
};
const labelStyle = { fontSize: 12.5, color: "#374151", display: "block", marginBottom: 5, fontWeight: 500 };
const cardStyle  = { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "18px 22px" };
const btnDark    = { padding: "10px 22px", borderRadius: 8, border: "none", background: "#1e3a5f", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnGhost   = { padding: "10px 18px", borderRadius: 8, border: "2px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13, color: "#374151", fontWeight: 600 };
const thStyle    = { background: "#f8fafc", textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#64748b", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" };
const tdStyle    = { padding: "10px 12px", borderBottom: "1px solid #f3f4f6", color: "#374151", whiteSpace: "nowrap" };

/* วันนี้แบบ local (ไม่ใช้ toISOString ตรงๆ — จะเลื่อนย้อน 1 วันในเขตเวลาไทย) */
function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
}

const fmtDate  = (iso) => (iso ? new Date(iso).toLocaleDateString("th-TH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-");
const fmtNum   = (n) => Number(n || 0).toLocaleString("th-TH");
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const names = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
  return `${names[Number(m) - 1]} ${String(Number(y) + 543).slice(-2)}`;
};

const CHART_TYPES = GUEST_TYPES.filter((g) => g.value !== "unknown").concat(
  GUEST_TYPES.filter((g) => g.value === "unknown")   // unknown ไว้ท้ายสุด แต่ไม่ซ่อน
);

export default function GuestRegistry() {
  const { lang } = useLanguage();

  const [tab, setTab] = useState("registry");   // "registry" | "analytics"

  /* filters (ใช้ร่วมกันทั้งสองแท็บ)
     วันที่เว้นว่าง = ไม่กรอง (เห็นลูกค้าทุกคน)
     ห้ามตั้งค่าเริ่มต้นเป็น "ถึงวันนี้" — การจองล่วงหน้าที่เช็คอินแล้วมีวันเข้าพักเป็นอนาคต
     จะถูกกรองหายหมดจนหน้าดูเหมือนพัง */
  const [q, setQ]               = useState("");
  const [type, setType]         = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");

  /* registry */
  const [data, setData]       = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  /* analytics */
  const [stats, setStats]           = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  /* drawer รายละเอียดลูกค้า */
  const [detail, setDetail]         = useState(null);   // { guest, stays, ... }
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing]       = useState(false);
  const [editForm, setEditForm]     = useState({});
  const [saving, setSaving]         = useState(false);

  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2400); };

  const buildParams = useCallback((p, limit) => {
    const params = new URLSearchParams();
    if (q.trim())  params.set("q", q.trim());
    if (type !== "all") params.set("type", type);
    if (dateFrom)  params.set("dateFrom", dateFrom);
    if (dateTo)    params.set("dateTo", dateTo);
    if (p != null) params.set("page", String(p));
    if (limit != null) params.set("limit", String(limit));
    return params;
  }, [q, type, dateFrom, dateTo]);

  const fetchList = useCallback(async (p = 1) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/guests?${buildParams(p, PAGE_SIZE)}`, { headers: authHeader() });
      if (!res.ok) throw new Error(t(lang, "grLoadFail"));
      const json = await res.json();
      setData(json);
      setPage(json.page || p);
    } catch (e) {
      setError(e.message);
      setData({ items: [], total: 0, page: 1, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [buildParams, lang]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo)   params.set("dateTo", dateTo);
      const res = await fetch(`${API}/api/report/guest-analytics?${params}`, { headers: authHeader() });
      if (!res.ok) throw new Error(t(lang, "grLoadFail"));
      setStats(await res.json());
    } catch (e) {
      showToast(e.message);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [dateFrom, dateTo, lang]);

  // โหลดครั้งแรกครั้งเดียว — ครั้งต่อไปโหลดตอนกดค้นหา ไม่ใช่ทุกครั้งที่พิมพ์
  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // แท็บวิเคราะห์โหลดข้อมูลตอนเปิดครั้งแรกเท่านั้น (ตัวเลขหนักกว่าตารางมาก)
  useEffect(() => {
    if (tab === "analytics" && !stats) fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleSearch = () => {
    fetchList(1);
    if (tab === "analytics") fetchStats();
    else setStats(null);   // ให้โหลดใหม่ตอนสลับไปแท็บวิเคราะห์
  };

  /* ── รายละเอียดลูกค้า ── */
  const openDetail = async (id) => {
    setDetailLoading(true); setEditing(false);
    try {
      const res = await fetch(`${API}/api/guests/${id}`, { headers: authHeader() });
      if (!res.ok) throw new Error(t(lang, "grLoadFail"));
      const json = await res.json();
      setDetail(json);
      setEditForm({ ...json.guest });
    } catch (e) {
      showToast(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const saveDetail = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/guests/${detail.guest._id}`, {
        method: "PATCH", headers: jsonHeader(), body: JSON.stringify(editForm),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || t(lang, "grSaveFail"));
      setDetail((d) => ({ ...d, guest: json.guest }));
      setEditing(false);
      showToast(t(lang, "grSaved"));
      fetchList(page);
    } catch (e) {
      showToast(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* ── Export Excel 3 sheets ── */
  const handleExport = async () => {
    setExporting(true);
    try {
      // ดึงทั้งหมด ไม่ใช่แค่หน้าที่เปิดอยู่
      const [guestsRes, staysRes, statsRes] = await Promise.all([
        fetch(`${API}/api/guests?${buildParams(1, 0)}`, { headers: authHeader() }),
        fetch(`${API}/api/report/guest-stays?${buildParams(null, null)}`, { headers: authHeader() }),
        fetch(`${API}/api/report/guest-analytics?${buildParams(null, null)}`, { headers: authHeader() }),
      ]);
      if (!guestsRes.ok || !staysRes.ok || !statsRes.ok) throw new Error(t(lang, "grExportFail"));

      const guests = (await guestsRes.json()).items || [];
      const stays  = await staysRes.json();
      const a      = await statsRes.json();

      if (guests.length === 0 && stays.length === 0) {
        showToast(t(lang, "grNoData"));
        return;
      }

      const wb = XLSX.utils.book_new();

      /* ── Sheet 1: ลูกค้า ── */
      const sheet1 = guests.map((g, i) => ({
        "#": i + 1,
        "ชื่อ-สกุล": g.fullName || "",
        "เลขบัตร/พาสปอร์ต": g.idCard || "",
        "ประเภทผู้เข้าพัก": guestTypeLabel(g.guestType, "th"),
        "เพศ": g.gender || "",
        "วันเกิด": g.birthDate || "",
        "สัญชาติ": g.nationality || "",
        "ประเทศ": g.country || "",
        "ที่อยู่": g.address || "",
        "โทรศัพท์": g.phone || "",
        "ชื่อบริษัท": g.companyName || "",
        "ที่อยู่บริษัท": g.companyAddress || "",
        "สาขาเลขที่": g.companyBranchNo || "",
        "เลขผู้เสียภาษี": g.companyTaxId || "",
        "ยี่ห้อรถ": g.carBrand || "",
        "สีรถ": g.carColor || "",
        "ทะเบียนรถ": g.carPlate || "",
        "จังหวัดทะเบียน": g.carProvince || "",
        "จำนวนครั้งที่เข้าพัก": g.stayCount || 0,
        "เข้าพักครั้งแรก": fmtDate(g.firstStayAt),
        "เข้าพักครั้งล่าสุด": fmtDate(g.lastStayAt),
        "ยอดรวมสะสม": g.totalAmount || 0,
        "ชำระแล้วรวม": g.totalPaid || 0,
        "ค้างชำระ": g.outstanding || 0,
        "หมายเหตุ": g.adminNote || "",
      }));
      const ws1 = XLSX.utils.json_to_sheet(sheet1);
      ws1["!cols"] = [
        { wch: 5 }, { wch: 24 }, { wch: 18 }, { wch: 18 }, { wch: 8 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 34 }, { wch: 14 }, { wch: 26 }, { wch: 30 },
        { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 16 },
        { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 24 },
      ];
      XLSX.utils.book_append_sheet(wb, ws1, "ลูกค้า");

      /* ── Sheet 2: ประวัติการเข้าพัก ── */
      const sheet2 = stays.map((s, i) => ({
        "#": i + 1,
        "ชื่อลูกค้า": s.guestName || "",
        "เลขบัตร": s.idCard || "",
        "โทรศัพท์": s.phone || "",
        "ประเภทผู้เข้าพัก": guestTypeLabel(s.guestType, "th"),
        "วัตถุประสงค์": stayPurposeLabel(s.stayPurpose, "th"),
        "สัญชาติ": s.nationality || "",
        "ห้อง": s.roomNumber || "",
        "ประเภทห้อง": s.roomTypeName || "",
        "วันที่เข้า": fmtDate(s.checkIn),
        "วันที่ออก": fmtDate(s.checkOut),
        "เช็คอินจริง": fmtDate(s.checkedInAt),
        "เช็คเอาท์จริง": fmtDate(s.checkedOutAt),
        "จำนวนคืน": s.nights,
        "ยอดรวม": s.totalAmount,
        "ชำระแล้ว": s.paidAmount,
        "ค้างชำระ": s.outstanding,
        "วิธีชำระ": s.paymentMethod === "cash" ? "เงินสด" : s.paymentMethod === "qr" ? "QR Code" : "",
        "ช่องทางจอง": s.channel === "walkin" ? "หน้าเคาน์เตอร์" : "ออนไลน์",
        "บริษัท": s.companyName || "",
        "เลขผู้เสียภาษี": s.companyTaxId || "",
        "ยี่ห้อรถ": s.carBrand || "",
        "สีรถ": s.carColor || "",
        "ทะเบียนรถ": s.carPlate || "",
        "จังหวัดทะเบียน": s.carProvince || "",
      }));
      const ws2 = XLSX.utils.json_to_sheet(sheet2);
      ws2["!cols"] = [
        { wch: 5 }, { wch: 24 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 16 },
        { wch: 12 }, { wch: 8 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 14 }, { wch: 26 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(wb, ws2, "ประวัติการเข้าพัก");

      /* ── Sheet 3: สรุปวิเคราะห์ (หลายบล็อกในชีตเดียว → ใช้ aoa) ── */
      const typeCols = CHART_TYPES.map((g) => g.value);
      const typeHead = CHART_TYPES.map((g) => g.th);
      const aoa = [];

      aoa.push([`สรุปวิเคราะห์ลูกค้า ${dateFrom || "-"} ถึง ${dateTo || "-"}`]);
      aoa.push([]);
      aoa.push(["ภาพรวม"]);
      aoa.push(["จำนวนการเข้าพัก", a.totals?.stays || 0]);
      aoa.push(["จำนวนลูกค้า", a.totals?.guests || 0]);
      aoa.push(["จำนวนคืนรวม", a.totals?.nights || 0]);
      aoa.push(["รายได้รวม (บาท)", a.totals?.revenue || 0]);
      aoa.push(["คืนเฉลี่ยต่อการเข้าพัก", a.totals?.avgNights || 0]);
      aoa.push(["ยอดใช้จ่ายเฉลี่ยต่อการเข้าพัก", a.totals?.avgSpend || 0]);
      aoa.push(["ลูกค้าใหม่", a.repeat?.firstTime || 0]);
      aoa.push(["ลูกค้ากลับมาซ้ำ", a.repeat?.repeat || 0]);
      aoa.push(["มารถส่วนตัว", a.carUsage?.withCar || 0]);
      aoa.push(["ไม่มีรถ", a.carUsage?.withoutCar || 0]);
      aoa.push(["จองหน้าเคาน์เตอร์", a.byChannel?.walkIn || 0]);
      aoa.push(["จองออนไลน์", a.byChannel?.online || 0]);
      aoa.push([]);

      aoa.push(["จำนวนการเข้าพักแยกตามประเภท (รายเดือน)"]);
      aoa.push(["เดือน", ...typeHead, "รวม"]);
      for (const m of a.byMonth || []) {
        aoa.push([fmtMonth(m.ym), ...typeCols.map((c) => m[c] || 0), m.total || 0]);
      }
      aoa.push([]);

      aoa.push(["รายได้แยกตามประเภท (รายเดือน, บาท)"]);
      aoa.push(["เดือน", ...typeHead, "รวม"]);
      for (const m of a.byMonth || []) {
        aoa.push([fmtMonth(m.ym), ...typeCols.map((c) => m[`${c}_revenue`] || 0), m.revenue || 0]);
      }
      aoa.push([]);

      aoa.push(["สรุปตามประเภทผู้เข้าพัก"]);
      aoa.push(["ประเภท", "จำนวนการเข้าพัก", "จำนวนลูกค้า", "จำนวนคืน", "รายได้ (บาท)", "สัดส่วน (%)"]);
      for (const r of a.byType || []) {
        aoa.push([guestTypeLabel(r.type, "th"), r.stays, r.guests, r.nights, r.revenue, r.share]);
      }
      aoa.push([]);

      aoa.push(["10 บริษัทที่เข้าพักบ่อยที่สุด"]);
      aoa.push(["ชื่อบริษัท", "จำนวนการเข้าพัก", "จำนวนคน", "รายได้ (บาท)"]);
      for (const r of a.topCompanies || []) aoa.push([r.companyName, r.stays, r.guests, r.revenue]);
      aoa.push([]);

      aoa.push(["ต้นทางลูกค้า (จังหวัดทะเบียนรถ)"]);
      aoa.push(["จังหวัด", "จำนวนการเข้าพัก"]);
      for (const r of a.byProvince || []) aoa.push([r.province, r.stays]);
      aoa.push([]);

      aoa.push(["สัญชาติ"]);
      aoa.push(["สัญชาติ", "จำนวนการเข้าพัก"]);
      for (const r of a.byNationality || []) aoa.push([r.nationality, r.stays]);
      aoa.push([]);

      aoa.push(["วัตถุประสงค์การเข้าพัก"]);
      aoa.push(["วัตถุประสงค์", "จำนวนการเข้าพัก", "สัดส่วน (%)"]);
      for (const r of a.byPurpose || []) {
        aoa.push([r.purpose ? stayPurposeLabel(r.purpose, "th") : "ไม่ระบุ", r.stays, r.share]);
      }

      const ws3 = XLSX.utils.aoa_to_sheet(aoa);
      ws3["!cols"] = [{ wch: 32 }, ...Array(typeCols.length + 1).fill({ wch: 16 })];
      XLSX.utils.book_append_sheet(wb, ws3, "สรุปวิเคราะห์");

      XLSX.writeFile(wb, `ทะเบียนลูกค้า_${dateFrom || "ทั้งหมด"}_ถึง_${dateTo || "ทั้งหมด"}.xlsx`);
      showToast(t(lang, "grExportOk"));
    } catch (e) {
      showToast(e.message);
    } finally {
      setExporting(false);
    }
  };

  /* ── สรุปหัวตาราง ── */
  const sumStays = data.items.reduce((s, g) => s + (g.stayCount || 0), 0);
  const sumDue   = data.items.reduce((s, g) => s + (g.outstanding || 0), 0);

  return (
    <>
      <NavbarAdmin />
      <div style={{ minHeight: "100vh", background: "#f1f3f6", fontFamily: "'Noto Sans Thai',sans-serif", padding: "24px 20px 60px" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ marginBottom: 18 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0 }}>
              👥 {t(lang, "grTitle")}
            </h1>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "5px 0 0" }}>{t(lang, "grSub")}</p>
          </div>

          {/* Tabs */}
          <div style={{ display: "inline-flex", background: "#e5e7eb", borderRadius: 10, padding: 3, gap: 2, marginBottom: 16 }}>
            {[["registry", t(lang, "grTabRegistry")], ["analytics", t(lang, "grTabAnalytics")]].map(([v, label]) => (
              <button key={v} onClick={() => setTab(v)} style={{
                border: "none", padding: "8px 20px", borderRadius: 8, cursor: "pointer",
                fontSize: 13.5, fontWeight: 600, fontFamily: "inherit",
                background: tab === v ? "#fff" : "transparent",
                color: tab === v ? "#0f172a" : "#6b7280",
                boxShadow: tab === v ? "0 1px 3px rgba(0,0,0,.1)" : "none",
              }}>{label}</button>
            ))}
          </div>

          {/* Filter card */}
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr auto auto", gap: 12, alignItems: "end" }}
                 className="gr-filter-grid">
              <div>
                <label style={labelStyle}>{t(lang, "grSearch")}</label>
                <input style={inputStyle} value={q} placeholder={t(lang, "grSearchPh")}
                       onChange={(e) => setQ(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "grType")}</label>
                <select style={inputStyle} value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="all">{t(lang, "grTypeAll")}</option>
                  {GUEST_TYPES.map((g) => (
                    <option key={g.value} value={g.value}>{lang === "en" ? g.en : g.th}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "grFrom")}</label>
                <input type="date" style={inputStyle} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "grTo")}</label>
                <input type="date" style={inputStyle} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <button style={btnDark} onClick={handleSearch} disabled={loading || statsLoading}>
                {loading || statsLoading ? t(lang, "grLoading") : t(lang, "grLoad")}
              </button>
              <button style={btnGhost} onClick={handleExport} disabled={exporting}>
                {exporting ? t(lang, "grExporting") : t(lang, "grExport")}
              </button>
            </div>
            {/* ช่วงวันที่สำเร็จรูป — แท็บวิเคราะห์ใช้ช่วงนี้ด้วย */}
            <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 11.5, color: "#9ca3af", marginRight: 2 }}>{t(lang, "grQuickRange")}</span>
              {[
                ["grRangeAll",   "", ""],
                ["grRange12m",   todayISO(-365), todayISO(365)],
                ["grRangeThisY", `${new Date().getFullYear()}-01-01`, `${new Date().getFullYear()}-12-31`],
                ["grRangeLastY", `${new Date().getFullYear() - 1}-01-01`, `${new Date().getFullYear() - 1}-12-31`],
              ].map(([key, from, to]) => {
                const on = dateFrom === from && dateTo === to;
                return (
                  <button key={key} onClick={() => { setDateFrom(from); setDateTo(to); }}
                    style={{
                      padding: "4px 12px", borderRadius: 999, cursor: "pointer", fontSize: 11.5,
                      fontFamily: "inherit", fontWeight: 600,
                      border: on ? "1px solid #1e3a5f" : "1px solid #e5e7eb",
                      background: on ? "#eef2f7" : "#fff",
                      color: on ? "#1e3a5f" : "#6b7280",
                    }}>{t(lang, key)}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 8 }}>{t(lang, "grFilterNote")}</div>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {/* ═══════════ แท็บทะเบียนลูกค้า ═══════════ */}
          {tab === "registry" && (
            <div style={cardStyle}>
              <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                <Stat label={t(lang, "grStatGuests")} value={fmtNum(data.total)} />
                <Stat label={t(lang, "grStatStays")}  value={fmtNum(sumStays)} />
                <Stat label={t(lang, "grStatDue")}    value={fmtNum(sumDue)} color={sumDue > 0 ? "#dc2626" : "#16a34a"} />
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1000 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>{t(lang, "grColName")}</th>
                      <th style={thStyle}>{t(lang, "grColType")}</th>
                      <th style={thStyle}>{t(lang, "grColIdCard")}</th>
                      <th style={thStyle}>{t(lang, "grColPhone")}</th>
                      <th style={thStyle}>{t(lang, "grColCompany")}</th>
                      <th style={thStyle}>{t(lang, "grColCar")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t(lang, "grColStays")}</th>
                      <th style={thStyle}>{t(lang, "grColLastStay")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t(lang, "grColTotal")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t(lang, "grColDue")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={11} style={{ ...tdStyle, textAlign: "center", padding: 40, color: "#9ca3af" }}>{t(lang, "grLoading")}</td></tr>
                    )}
                    {!loading && data.items.length === 0 && (
                      <tr><td colSpan={11} style={{ ...tdStyle, textAlign: "center", padding: 40, color: "#9ca3af" }}>{t(lang, "grEmpty")}</td></tr>
                    )}
                    {!loading && data.items.map((g, i) => (
                      <tr key={g._id} onClick={() => openDetail(g._id)}
                          style={{ background: i % 2 ? "#fafafa" : "#fff", cursor: "pointer" }}>
                        <td style={tdStyle}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: "#0f172a" }}>{g.fullName || "—"}</td>
                        <td style={tdStyle}>
                          <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "#fff", background: guestTypeColor(g.guestType) }}>
                            {guestTypeLabel(g.guestType, lang)}
                          </span>
                        </td>
                        <td style={tdStyle}>{g.idCard || "—"}</td>
                        <td style={tdStyle}>{g.phone || "—"}</td>
                        <td style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{g.companyName || "—"}</td>
                        <td style={tdStyle}>
                          {g.carPlate ? `${g.carPlate}${g.carProvince ? ` · ${g.carProvince}` : ""}` : "—"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{g.stayCount || 0}</td>
                        <td style={tdStyle}>{fmtDate(g.lastStayAt)}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{fmtNum(g.totalAmount)}</td>
                        <td style={{ ...tdStyle, textAlign: "right", color: g.outstanding > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
                          {g.outstanding > 0 ? fmtNum(g.outstanding) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.pages > 1 && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 16 }}>
                  <button style={btnGhost} disabled={page <= 1} onClick={() => fetchList(page - 1)}>‹</button>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>{page} / {data.pages}</span>
                  <button style={btnGhost} disabled={page >= data.pages} onClick={() => fetchList(page + 1)}>›</button>
                </div>
              )}
            </div>
          )}

          {/* ═══════════ แท็บวิเคราะห์ ═══════════ */}
          {tab === "analytics" && (
            <Analytics stats={stats} loading={statsLoading} lang={lang} />
          )}
        </div>
      </div>

      {/* Drawer รายละเอียดลูกค้า */}
      {(detail || detailLoading) && (
        <GuestDetail
          detail={detail} loading={detailLoading} lang={lang}
          editing={editing} setEditing={setEditing}
          editForm={editForm} setEditForm={setEditForm}
          saving={saving} onSave={saveDetail}
          onClose={() => { setDetail(null); setEditing(false); }}
        />
      )}

      <Toast show={!!toast} type="info" message={toast} lang={lang} />

      <style>{`
        @media (max-width: 1000px) {
          .gr-filter-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 600px) {
          .gr-filter-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

/* ── ตัวเลขสรุปเล็กๆ ── */
function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: "#9ca3af", fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || "#0f172a" }}>{value}</div>
    </div>
  );
}

/* ══════════════ แท็บวิเคราะห์ ══════════════ */
function Analytics({ stats, loading, lang }) {
  if (loading) {
    return <div style={{ ...cardStyle, textAlign: "center", padding: 60, color: "#9ca3af" }}>{t(lang, "grLoading")}</div>;
  }
  if (!stats || !stats.totals?.stays) {
    return <div style={{ ...cardStyle, textAlign: "center", padding: 60, color: "#9ca3af" }}>{t(lang, "grNoData")}</div>;
  }

  const { totals, byType, byMonth, byProvince, topCompanies, carUsage, repeat, byChannel } = stats;
  const shownTypes = CHART_TYPES.filter((g) => byType.some((r) => r.type === g.value && r.stays > 0));

  const chartCard = { ...cardStyle, padding: "18px 20px" };
  const chartTitle = { fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 2 };
  const chartSub = { fontSize: 11.5, color: "#9ca3af", marginBottom: 14 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Stat tiles */}
      <div style={{ ...cardStyle, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 18 }}>
        <Stat label={t(lang, "grStatStays")}     value={fmtNum(totals.stays)} />
        <Stat label={t(lang, "grStatGuests")}    value={fmtNum(totals.guests)} />
        <Stat label={t(lang, "grStatRevenue")}   value={fmtNum(totals.revenue)} color="#16a34a" />
        <Stat label={t(lang, "grStatAvgNights")} value={totals.avgNights} />
        <Stat label={t(lang, "grStatAvgSpend")}  value={fmtNum(totals.avgSpend)} />
        <Stat label={t(lang, "grStatNewGuest")}  value={`${fmtNum(repeat.firstTime)} / ${fmtNum(repeat.repeat)}`} />
        <Stat label={t(lang, "grStatCar")}       value={`${fmtNum(carUsage.withCar)} / ${fmtNum(carUsage.withoutCar)}`} />
        <Stat label={t(lang, "grStatChannel")}   value={`${fmtNum(byChannel.walkIn)} / ${fmtNum(byChannel.online)}`} />
      </div>

      {/* Donut + สรุปตามประเภท */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px,380px) 1fr", gap: 16 }} className="gr-chart-row">
        <div style={chartCard}>
          <div style={chartTitle}>{t(lang, "grChartMix")}</div>
          <div style={chartSub}>{t(lang, "grChartMixSub")}</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={byType.filter((r) => r.stays > 0)} dataKey="stays" nameKey="type"
                   cx="50%" cy="50%" innerRadius={58} outerRadius={92} paddingAngle={2}>
                {byType.filter((r) => r.stays > 0).map((r) => (
                  <Cell key={r.type} fill={guestTypeColor(r.type)} />
                ))}
              </Pie>
              <Tooltip formatter={(v, n) => [`${fmtNum(v)} ${t(lang, "grUnitStays")}`, guestTypeLabel(n, lang)]} />
              <Legend formatter={(v) => guestTypeLabel(v, lang)} iconType="circle"
                      wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={chartCard}>
          <div style={chartTitle}>{t(lang, "grChartRevenue")}</div>
          <div style={chartSub}>{t(lang, "grChartRevenueSub")}</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byType.filter((r) => r.stays > 0)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="type" tickFormatter={(v) => guestTypeLabel(v, lang)}
                     tick={{ fontSize: 11 }} interval={0} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNum(v)} />
              <Tooltip formatter={(v) => fmtNum(v)} labelFormatter={(v) => guestTypeLabel(v, lang)} />
              <Bar dataKey="revenue" name={t(lang, "grUnitBaht")} radius={[6, 6, 0, 0]}>
                {byType.filter((r) => r.stays > 0).map((r) => (
                  <Cell key={r.type} fill={guestTypeColor(r.type)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stacked bar รายเดือน — กราฟหลักที่ตอบว่าใครพีคเดือนไหน */}
      <div style={chartCard}>
        <div style={chartTitle}>{t(lang, "grChartMonthly")}</div>
        <div style={chartSub}>{t(lang, "grChartMonthlySub")}</div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={byMonth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="ym" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip labelFormatter={fmtMonth}
                     formatter={(v, n) => [`${fmtNum(v)} ${t(lang, "grUnitStays")}`, guestTypeLabel(n, lang)]} />
            <Legend formatter={(v) => guestTypeLabel(v, lang)} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            {shownTypes.map((g) => (
              <Bar key={g.value} dataKey={g.value} stackId="s" fill={g.color} name={g.value} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* บริษัท + จังหวัด */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="gr-chart-row">
        <div style={chartCard}>
          <div style={chartTitle}>{t(lang, "grChartCompanies")}</div>
          <div style={chartSub}>{t(lang, "grChartCompaniesSub")}</div>
          {topCompanies.length === 0
            ? <Empty lang={lang} />
            : (
              <ResponsiveContainer width="100%" height={Math.max(180, topCompanies.length * 34)}>
                <BarChart data={topCompanies} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="companyName" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${fmtNum(v)} ${t(lang, "grUnitStays")}`} />
                  <Bar dataKey="stays" fill="#3b82f6" radius={[0, 6, 6, 0]} name={t(lang, "grUnitStays")} />
                </BarChart>
              </ResponsiveContainer>
            )}
        </div>

        <div style={chartCard}>
          <div style={chartTitle}>{t(lang, "grChartProvince")}</div>
          <div style={chartSub}>{t(lang, "grChartProvinceSub")}</div>
          {byProvince.length === 0
            ? <Empty lang={lang} />
            : (
              <ResponsiveContainer width="100%" height={Math.max(180, byProvince.length * 30)}>
                <BarChart data={byProvince} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="province" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${fmtNum(v)} ${t(lang, "grUnitStays")}`} />
                  <Bar dataKey="stays" fill="#06b6d4" radius={[0, 6, 6, 0]} name={t(lang, "grUnitStays")} />
                </BarChart>
              </ResponsiveContainer>
            )}
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10, lineHeight: 1.6 }}>
            {t(lang, "grProvinceCaveat")}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .gr-chart-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Empty({ lang }) {
  return <div style={{ padding: "40px 0", textAlign: "center", color: "#cbd5e1", fontSize: 13 }}>{t(lang, "grNoData")}</div>;
}

/* ══════════════ Drawer รายละเอียดลูกค้า ══════════════ */
function GuestDetail({ detail, loading, lang, editing, setEditing, editForm, setEditForm, saving, onSave, onClose }) {
  const g = detail?.guest;

  const field = (labelKey, key, opts = {}) => (
    <div key={key} style={{ marginBottom: 12 }}>
      <label style={labelStyle}>{t(lang, labelKey)}</label>
      {editing ? (
        opts.select ? (
          <SearchableSelect
            value={editForm[key] || ""}
            onChange={(v) => setEditForm((f) => ({ ...f, [key]: v }))}
            options={opts.select}
            placeholder={opts.searchPh || "—"}
            inputStyle={inputStyle}
          />
        ) : (
          <input style={inputStyle} value={editForm[key] || ""}
                 onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))} />
        )
      ) : (
        <div style={{ fontSize: 13.5, color: "#111827", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
          {opts.render ? opts.render(g?.[key]) : (g?.[key] || "—")}
        </div>
      )}
    </div>
  );

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000,
      display: "flex", justifyContent: "flex-end",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(760px, 100%)", background: "#fff", height: "100%", overflowY: "auto",
        padding: "26px 30px 60px", fontFamily: "'Noto Sans Thai',sans-serif",
      }}>
        {loading || !g ? (
          <div style={{ textAlign: "center", padding: 80, color: "#9ca3af" }}>{t(lang, "grLoading")}</div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 19, fontWeight: 700, margin: 0, color: "#0f172a" }}>{g.fullName || "—"}</h2>
                <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ display: "inline-block", padding: "3px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, color: "#fff", background: guestTypeColor(g.guestType) }}>
                    {guestTypeLabel(g.guestType, lang)}
                  </span>
                  <span style={{ fontSize: 12.5, color: "#6b7280" }}>
                    {t(lang, "grDetailStays", detail.stayCount || 0)}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {editing ? (
                  <>
                    <button style={btnGhost} onClick={() => { setEditing(false); setEditForm({ ...g }); }}>
                      {t(lang, "grCancel")}
                    </button>
                    <button style={btnDark} onClick={onSave} disabled={saving}>
                      {saving ? t(lang, "grSaving") : t(lang, "grSave")}
                    </button>
                  </>
                ) : (
                  <button style={btnGhost} onClick={() => setEditing(true)}>{t(lang, "grEdit")}</button>
                )}
                <button style={{ ...btnGhost, border: "none", fontSize: 20, padding: "4px 12px" }} onClick={onClose}>×</button>
              </div>
            </div>

            {/* สรุปยอด */}
            <div style={{ display: "flex", gap: 26, flexWrap: "wrap", background: "#f8fafc", borderRadius: 10, padding: "14px 18px", marginBottom: 22 }}>
              <Stat label={t(lang, "grColStays")}    value={fmtNum(detail.stayCount)} />
              <Stat label={t(lang, "grColTotal")}    value={fmtNum(detail.totalAmount)} />
              <Stat label={t(lang, "grDetailPaid")}  value={fmtNum(detail.totalPaid)} color="#16a34a" />
              <Stat label={t(lang, "grColDue")}      value={fmtNum(detail.outstanding)} color={detail.outstanding > 0 ? "#dc2626" : "#16a34a"} />
              <Stat label={t(lang, "grDetailFirst")} value={fmtDate(detail.firstStayAt)} />
            </div>

            <Section title={t(lang, "grSecIdentity")} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 22px" }}>
              {field("grColName", "fullName")}
              {field("grColIdCard", "idCard")}
              {field("ciFieldPhone", "phone")}
              {field("ciFieldBirthDate", "birthDate")}
              {field("ciFieldGender", "gender")}
              {field("ciFieldNationality", "nationality")}
              {field("ciFieldCountry", "country")}
              {field("ciGuestType", "guestType", {
                select: GUEST_TYPES.map((x) => ({ value: x.value, label: lang === "en" ? x.en : x.th })),
                render: (v) => guestTypeLabel(v, lang),
              })}
            </div>
            {field("ciFieldAddress", "address")}

            <Section title={t(lang, "ciCompanyGroup")} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 22px" }}>
              {field("ciCompanyName", "companyName")}
              {field("ciCompanyTaxId", "companyTaxId")}
              {field("ciCompanyAddress", "companyAddress")}
              {field("ciCompanyBranchNo", "companyBranchNo")}
            </div>

            <Section title={t(lang, "ciCarGroup")} note={t(lang, "ciCarNote")} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 22px" }}>
              {field("ciCarPlate", "carPlate")}
              {field("ciCarProvince", "carProvince", { select: TH_PROVINCES, searchPh: t(lang, "ciProvinceSearchPh") })}
              {field("ciCarBrand", "carBrand")}
              {field("ciCarColor", "carColor")}
            </div>

            <Section title={t(lang, "grSecNote")} />
            {field("grColNote", "adminNote")}

            {/* ประวัติการเข้าพัก */}
            <Section title={t(lang, "grSecHistory")} />
            <div style={{ overflowX: "auto", border: "1px solid #eceff2", borderRadius: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 640 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t(lang, "ciHistDate")}</th>
                    <th style={thStyle}>{t(lang, "ciHistRoom")}</th>
                    <th style={thStyle}>{t(lang, "ciHistType")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t(lang, "grColNights")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t(lang, "ciHistTotal")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t(lang, "ciHistPaid")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t(lang, "ciHistDue")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.stays || []).length === 0 && (
                    <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", padding: 30, color: "#9ca3af" }}>{t(lang, "grNoData")}</td></tr>
                  )}
                  {(detail.stays || []).map((s, i) => (
                    <tr key={s._id} style={{ background: i % 2 ? "#fafafa" : "#fff" }}>
                      <td style={tdStyle}>{fmtDate(s.checkIn)} – {fmtDate(s.checkOut)}</td>
                      <td style={tdStyle}>{s.roomNumber || "—"}</td>
                      <td style={tdStyle}>
                        <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 600, color: "#fff", background: guestTypeColor(s.guestType) }}>
                          {guestTypeLabel(s.guestType, lang)}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{s.nights}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{fmtNum(s.totalAmount)}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{fmtNum(s.paidAmount)}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: s.outstanding > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
                        {s.outstanding > 0 ? fmtNum(s.outstanding) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, note }) {
  return (
    <div style={{ margin: "22px 0 12px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</div>
      {note && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{note}</div>}
    </div>
  );
}
